import useSWR from 'swr'
import {
  Prisma,
  Tier,
  Exhibition,
  Work,
  Config,
  MediaPlayer,
  MediaPlayerLog,
  MediaPlayerCodeInjection,
  Feature,
  SRT,
  GeoFence,
  NetmaskFence,
  Track
} from '@prisma/client'
import { CustomError } from 'ts-custom-error'
import parseSRT from 'parse-srt'
import { WorkWithParsedSrts, WorkWithDetails } from '@src/pages/api/work/[id]/index'
import { AudioUploadResults } from '@src/pages/api/upload'
import { LanguageOption } from '@src/api/translation/languages'
import { logger } from './logger'
import { TimelineEvent } from './api/mediaplayer'

// Generic interface for data fetched with SWR library
interface SwrData<T> {
  data: T;
  isLoading: boolean;
  isError: boolean;
  error: HttpError;
  mutate: (
    data?: (cur: T) => T,
    shouldRevalidate?: boolean
  ) => Promise<unknown>
}

export class HttpError extends CustomError {
  public constructor(
    public code: number,
    message?: string
  ) {
    super(message)
  }
}

const createErrorResponse = async (res: Response) => {
  // If the status code is not in the range 200-299,
  // we still try to parse and throw it.
  if (!res.ok) {
    // Attach extra info to the error object.
    const info = await res.json()
    const error = new HttpError(res.status, info?.message)
    return error
  }
  return null
}

const handleResponse = async (res: Response) => {
  const error = await createErrorResponse(res)
  if (error) return { error, data: null }
  const data = await res.json()
  return { data, error: null }
}

const fetcher = async (url: string) => {
  const res = await fetch(url)
  const error = await createErrorResponse(res)
  if (error) throw error
  return res.json()
}

export function isNumeric(num: unknown): boolean {
  const numStr = `${num}` // coerce num to be a string
  return !Number.isNaN(numStr) && !Number.isNaN(parseFloat(numStr))
}

const API_ROOT = '/api'

type WithoutIds<T> = Omit<T, 'tenantId' | 'id'>
type UpdatableWorkFields = Omit<Work & { srts: SRT[] }, 'id' | 'tenantId' | 'lastTimestamp' | 'sortOrder'>
type UpdatableExhibitionFields = Omit<Exhibition, 'id' | 'tenantId' | 'sortOrder'>
type UpdatableMediaPlayerFields = Omit<MediaPlayer, 'id' | 'tenantId'>
type UpdatableConfigFields = WithoutIds<Config> & { geoFence?: WithoutIds<GeoFence>, netmaskFence?: WithoutIds<NetmaskFence> }

const LEGACY_MEDIA_PLAYER_FIELDS = {
  startupleaderdelay: 'startupLeaderDelay',
  looppointleaderdelay: 'loopPointLeaderDelay',
  workID: 'workId',
  videoURL: 'videoUrl',
  syncURL: 'syncUrl',
  serialnumber: 'serialNumber',
  videopath: 'videoPath',
}

export const mapLegacyKey = (k: string): string => {
  if (k in LEGACY_MEDIA_PLAYER_FIELDS) {
    return LEGACY_MEDIA_PLAYER_FIELDS[k]
  }
  return k
}

interface MediaPlayerWithLegacyFields extends MediaPlayer {
  startupleaderdelay?: UpdatableMediaPlayerFields['startupLeaderDelay'];
  looppointleaderdelay?: UpdatableMediaPlayerFields['loopPointLeaderDelay'];
  workID?: UpdatableMediaPlayerFields['workId'];
  videoURL?: UpdatableMediaPlayerFields['videoUrl'];
  syncURL?: UpdatableMediaPlayerFields['syncUrl'];
  serialnumber?: UpdatableMediaPlayerFields['serialNumber'];
  videopath?: UpdatableMediaPlayerFields['videoPath'];
}

export const addLegacyMediaPlayerFields = (mp: MediaPlayer): MediaPlayerWithLegacyFields => {
  const mediaPlayerWithLegacyFields: MediaPlayerWithLegacyFields = mp as MediaPlayerWithLegacyFields
  mediaPlayerWithLegacyFields.startupleaderdelay = mp.startupLeaderDelay
  mediaPlayerWithLegacyFields.looppointleaderdelay = mp.loopPointLeaderDelay
  mediaPlayerWithLegacyFields.workID = mp.workId
  mediaPlayerWithLegacyFields.videoURL = mp.videoUrl
  mediaPlayerWithLegacyFields.syncURL = mp.syncUrl
  mediaPlayerWithLegacyFields.serialnumber = mp.serialNumber
  mediaPlayerWithLegacyFields.videopath = mp.videoPath
  return mediaPlayerWithLegacyFields
}

export const getLastCaptionTime = (srt: string): number | null => {
  try {
    const parsedSrt = parseSRT(srt)
    const lastCaptionEnd = parsedSrt[parsedSrt.length - 1].end * 1000
    return lastCaptionEnd
  } catch (err) {
    return null
  }
}

export const normalizeDurationOrGetDefault = (
  duration: unknown,
  srt: string,
  lastTimestamp: bigint
): number => {
  if (isNumeric(duration)) return Number(duration)
  if (srt && !lastTimestamp) {
    const lastCaptionTime = getLastCaptionTime(srt)
    if (lastCaptionTime) return lastCaptionTime + 2000 // extra time so loop finishes?
  }
  return 3600000
}

export const getUpdatableConfigFields = (config: ConfigWithFences): UpdatableConfigFields => {
  const {
    organizationName,
    loaderSvgUrl,
    audioOnSvgUrl,
    audioOffSvgUrl,
    downArrowSvgUrl,
    leftArrowSvgUrl,
    audioDescriptionSvgUrl,
    closedCaptionsSvgUrl,
    indexBacklinkText,
    indexBacklinkUrl,
    instructions,
    title,
    guestStylesheetUrl,
    faviconUrl,
    googleAnalyticsId,
    serverSyncPassword,
    geoFence: maybeGeoFence,
    netmaskFence: maybeNetmaskFence,

  } = config
  const geoFence: WithoutIds<GeoFence> | undefined = maybeGeoFence ? {
    lat: Number(maybeGeoFence.lat),
    lon: Number(maybeGeoFence.lon),
    radius: Number(maybeGeoFence.radius),
  } : undefined
  const netmaskFence: WithoutIds<NetmaskFence> | undefined = maybeNetmaskFence ? {
    message: maybeNetmaskFence.message,
    mask: maybeNetmaskFence.mask,
  } : undefined

  return {
    organizationName,
    loaderSvgUrl,
    audioOffSvgUrl,
    audioOnSvgUrl,
    downArrowSvgUrl,
    leftArrowSvgUrl,
    audioDescriptionSvgUrl,
    closedCaptionsSvgUrl,
    indexBacklinkText,
    indexBacklinkUrl,
    instructions,
    title,
    guestStylesheetUrl,
    faviconUrl,
    googleAnalyticsId,
    serverSyncPassword,
    geoFence,
    netmaskFence,
  }
}

export const getUpdateableWorkFields = (work: Work & { srts: SRT[] }): UpdatableWorkFields => {
  const {
    title,
    exhibitionId,
    audioUrl,
    isActive,
    duration,
    nudge,
    srt,
    srts,
    dilation,
    backlinkUrl,
    loopingWork,
  } = work
  return {
    title,
    exhibitionId: exhibitionId ? Number(exhibitionId) : null,
    audioUrl,
    loopingWork,
    isActive,
    srt,
    srts,
    duration: normalizeDurationOrGetDefault(duration, srt, work.lastTimestamp),
    nudge: nudge ? Number(nudge) : 0,
    dilation: dilation ? Math.max(0.01, Number(dilation)) : 1,
    backlinkUrl,
  }
}

export const getUpdateableExhibitionFields = (exhibition: Exhibition):
  UpdatableExhibitionFields => {
  const {
    name, isActive, backlinkUrl, heroImageUrl, heroImageAltText, secondaryImageUrl,
    secondaryImageAltText, showStatus, workLabel,

  } = exhibition
  return {
    name,
    isActive,
    backlinkUrl,
    heroImageUrl,
    heroImageAltText,
    secondaryImageUrl,
    secondaryImageAltText,
    showStatus,
    workLabel: workLabel || 'work',
  }
}

export const getUpdatableMediaPlayerFields = (mediaplayer: MediaPlayerWithLegacyFields):
  UpdatableMediaPlayerFields => {
  const {
    nickname,
    lastTimestamp,
    syncMode,
    syncGroup,
    commandPort,
    volume,
    quietMode,
    updateWeb,
    updateWork: updateWorkBool,
    workId,
    workID,
    syncUrl,
    syncURL,
    videoPath,
    videopath,
    firmwareUrl,
    ipAddress,
    macAddress,
    serialNumber,
    serialnumber,
    duration,
    videoUrl,
    videoURL,
    startupLeaderDelay,
    startupleaderdelay,
    loopPointLeaderDelay,
    looppointleaderdelay,
    pollForConfigChanges,
    pollForCodeInjection,
    downloadNewContent,
    onScreenSubtitlesActive,
    onScreenSubtitlesShortDescriptor,
    bundleVersion,
    playerPackageVersion,
    playerType,
    playerDetails,
  } = mediaplayer
  return {
    nickname: nickname ?? 'mediaplayer',
    syncGroup: syncGroup ? Number(syncGroup) : 127,
    loopPointLeaderDelay: loopPointLeaderDelay || looppointleaderdelay
      ? Number(loopPointLeaderDelay || looppointleaderdelay)
      : 100,
    startupLeaderDelay: startupLeaderDelay || startupleaderdelay
      ? Number(startupLeaderDelay || startupleaderdelay)
      : 20000,
    syncMode: syncMode ?? 'solo',
    commandPort: commandPort ? Number(commandPort) : 9500,
    volume: volume ? Number(volume) : 15,
    quietMode: quietMode ? Number(quietMode) : 1,
    updateWeb: updateWeb ?? 'on',
    updateWork: updateWorkBool !== null ? updateWorkBool : true,
    workId: workId || workID ? Number(workId || workID) : null, // TODO: double check no knock on effects...
    syncUrl: (syncUrl || syncURL) ?? process.env.SERVER_HOST,
    videoPath: (videoPath || videopath) ?? 'auto',
    videoUrl: (videoUrl || videoURL),
    firmwareUrl,
    ipAddress,
    macAddress,
    lastTimestamp,
    serialNumber: serialNumber || serialnumber,
    duration,
    pollForConfigChanges,
    pollForCodeInjection,
    downloadNewContent,
    onScreenSubtitlesActive,
    onScreenSubtitlesShortDescriptor: onScreenSubtitlesShortDescriptor || null,
    bundleVersion,
    playerPackageVersion,
    playerType,
    playerDetails,
  }
}

// Exhibitions
const exhibitionWithWorks = Prisma.validator<Prisma.ExhibitionArgs>()({
  include: {
    works: {
      include: {
        tracks: true,
        mediaPlayers: true,
      },
    },
  },
})

export type ExhibitionWithWorks = Prisma.ExhibitionGetPayload<typeof exhibitionWithWorks>

export function useExhibitions(includeInactive?: boolean): SwrData<ExhibitionWithWorks[]> {
  // only authed users will be able to view inactive exhibitions and shows
  let exhibitionUrl = `${API_ROOT}/exhibition`
  if (includeInactive) exhibitionUrl += '?includeInactive=true'
  const { data, error, mutate } = useSWR(exhibitionUrl, fetcher)

  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

export function useExhibition(id: number): SwrData<ExhibitionWithWorks> {
  // only authed users will be able to view inactive exhibitions and shows
  const exhibitionUrl = `${API_ROOT}/exhibition/${id}`
  const { data, error, mutate } = useSWR(id ? exhibitionUrl : null, fetcher)

  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

type UpdateExhibitionData = {
  error?: HttpError;
  data?: Exhibition;
}

type UpdateMediaPlayerData = {
  error?: HttpError;
  data?: MediaPlayer;
}

type UpdateWorkData = {
  error?: HttpError;
  data?: Work;
}

export async function createExhibition(exhibition: Exhibition): Promise<UpdateExhibitionData> {
  const res = await fetch(`${API_ROOT}/exhibition`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(exhibition),
  })
  return handleResponse(res)
}

export async function updateExhibition(
  id: number, exhibition: Exhibition
): Promise<UpdateExhibitionData> {
  const res = await fetch(`${API_ROOT}/exhibition/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(getUpdateableExhibitionFields(exhibition)),
  })
  return handleResponse(res)
}

export async function deleteExhibition(id: number): Promise<UpdateExhibitionData> {
  const res = await fetch(`${API_ROOT}/exhibition/${id}`, {
    method: 'DELETE',
  })
  return handleResponse(res)
}

export async function updateExhibitionQuietMode(
  id: number, factor: number
): Promise<UpdateExhibitionData> {
  const res = await fetch(`${API_ROOT}/exhibition/${id}/quietmode`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ quietMode: factor }),
  })
  return handleResponse(res)
}
// Works
type WorkWithAutoTranslation = {
  work: Work & { srts: SRT[], tracks: Track[] }
  autoTranslationLanguages: LanguageOption[]
}

export type CreateWorkInput = WorkWithAutoTranslation
export type CreateWorkRequestBody = WorkWithAutoTranslation

export interface UpdateWorkInput extends WorkWithAutoTranslation {
  id: number
}
export type UpdateWorkRequestBody = {
  work: UpdatableWorkFields,
  autoTranslationLanguages: LanguageOption[]
}

export type UpdateWorkResponse = {
  data?: Work,
  error?: HttpError
}

export async function createWork({ work, autoTranslationLanguages }: CreateWorkInput): Promise<UpdateWorkResponse> {
  const data: CreateWorkRequestBody = {
    work,
    autoTranslationLanguages,
  }
  const res = await fetch(`${API_ROOT}/work`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleResponse(res)
}

export async function updateWork({ id, work, autoTranslationLanguages }: UpdateWorkInput): Promise<UpdateWorkResponse> {
  const data: UpdateWorkRequestBody = {
    work: getUpdateableWorkFields(work),
    autoTranslationLanguages,
  }
  const res = await fetch(`${API_ROOT}/work/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleResponse(res)
}

export async function deleteWork(id: number): Promise<UpdateWorkData> {
  const res = await fetch(`${API_ROOT}/work/${id}`, {
    method: 'DELETE',
  })
  return handleResponse(res)
}

export async function reorderWorks(workIdsInOrder: Array<number>): Promise<void> {
  const res = await fetch(`${API_ROOT}/work/reorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(workIdsInOrder),
  })
  const updated = res.json()
  return updated
}

export function useWork(id: number, includeInactive: boolean): SwrData<WorkWithParsedSrts> {
  let workUrl = `${API_ROOT}/work/${id}`
  if (includeInactive) workUrl += '?includeInactive=true'
  const { data, error, mutate } = useSWR(id ? workUrl : null, fetcher)
  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

export type UpdateTrackResponse = {
  data?: Track,
  error?: HttpError
}

export async function updateTrack(data: Track): Promise<UpdateTrackResponse> {
  const res = await fetch(`${API_ROOT}/track/${data.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleResponse(res)
}

export async function deleteTrack(trackId: number): Promise<UpdateTrackResponse> {
  const res = await fetch(`${API_ROOT}/track/${trackId}`, {
    method: 'DELETE',
  })
  return handleResponse(res)
}

export async function deleteSrt(srtId: number): Promise<{ error?: { message: string } }> {
  const res = await fetch(`${API_ROOT}/srt/${srtId}`, {
    method: 'DELETE',
  })
  return handleResponse(res)
}

export interface UseWorksOptions {
  exhibitionId?: number | null | undefined,
  includeInactive?: boolean,
  includeExhibitions?: boolean,
  includePlayers?: boolean
  includeTracks?: boolean
  includeSrts?: boolean
}

const workWithPlayers = Prisma.validator<Prisma.WorkArgs>()({
  include: {
    exhibition: true, srts: true, tracks: true, mediaPlayers: true,
  },
})

export type WorkWithPlayers = Prisma.WorkGetPayload<typeof workWithPlayers>

export function useWorks({
  exhibitionId,
  includeInactive,
  includeExhibitions,
  includePlayers,
  includeTracks,
  includeSrts,
}: UseWorksOptions = {}): SwrData<WorkWithDetails[]> {
  const workUrl = `${API_ROOT}/work/?`
  const params = new URLSearchParams()
  if (includeInactive) params.set('includeInactive', 'true')
  if (exhibitionId) params.set('exhibitionId', String(exhibitionId))
  if (exhibitionId === null) params.set('exhibitionId', 'null')
  if (includeExhibitions) params.set('includeExhibitions', 'true')
  if (includePlayers) params.set('includePlayers', 'true')
  if (includeTracks) params.set('includeTracks', 'true')
  if (includeSrts) params.set('includeSrts', 'true')
  const { data, error, mutate } = useSWR(workUrl + params.toString(), fetcher)
  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

// Media Players

const mediaPlayerWithWork = Prisma.validator<Prisma.MediaPlayerArgs>()({
  include: { work: true },
})

const mediaPlayerWithWorkAndLogs = Prisma.validator<Prisma.MediaPlayerArgs>()({
  include: { logs: true, work: true },
})

export type MediaPlayerWithWork = Prisma.MediaPlayerGetPayload<typeof mediaPlayerWithWork>

export type MediaPlayerWithWorkAndLogs =
  Prisma.MediaPlayerGetPayload<typeof mediaPlayerWithWorkAndLogs>

export interface UseMediaPlayerOptions {
  workId?: number | null | undefined,
  refreshInterval?: number | null | undefined
}

export function useMediaPlayers({ workId, refreshInterval }: UseMediaPlayerOptions = {}):
  SwrData<MediaPlayerWithWorkAndLogs[]> {
  // only authed users will be able to view inactive exhibitions and shows
  const mediaPlayersURL = `${API_ROOT}/mediaplayer?`
  const params = new URLSearchParams()
  if (workId) params.set('workId', String(workId))
  if (workId === null) params.set('workId', 'null')
  const { data, error, mutate } = useSWR(
    mediaPlayersURL + params.toString(),
    fetcher,
    refreshInterval ? { refreshInterval: 1000 } : undefined
  )

  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

export function useMediaPlayer(id: number, includeWork?: boolean): SwrData<MediaPlayerWithWork> {
  const mediaPlayerURL = `${API_ROOT}/mediaplayer/${id}${includeWork ? '?includeWork=true' : ''}`
  const { data, error, mutate } = useSWR(
    id ? mediaPlayerURL : null,
    fetcher
  )
  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

export async function createMediaPlayer(mediaplayer: MediaPlayer): Promise<MediaPlayer> {
  const res = await fetch(`${API_ROOT}/mediaplayer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mediaplayer),
  })
  const updated = await res.json()
  return updated
}

export async function updateMediaPlayer(
  id: number, mediaplayer: MediaPlayer
): Promise<MediaPlayer> {
  const res = await fetch(`${API_ROOT}/mediaplayer/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(getUpdatableMediaPlayerFields(mediaplayer)),
  })
  const updated = res.json()
  return updated
}

export async function deleteMediaPlayer(id: number): Promise<UpdateMediaPlayerData> {
  const res = await fetch(`${API_ROOT}/mediaplayer/${id}`, {
    method: 'DELETE',
  })
  return handleResponse(res)
}

export type ConfigManyMediaPlayerArgs = {
  exhibitionId?: number | null | undefined,
  workId?: number | null | undefined,
  data: {
    pollForConfigChanges?: boolean,
    pollForCodeInjection?: boolean
  }
}

export async function configManyMediaPlayers(args: ConfigManyMediaPlayerArgs) {
  const res = await fetch(`${API_ROOT}/mediaplayer/config-many`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  })
  const updated = res.json()
  return updated
}

// Logs

export function useMediaPlayerLog(id: number): SwrData<MediaPlayerLog[]> {
  const mediaPlayerLogURL = `${API_ROOT}/mediaplayer/${id}/log`
  const { data, error, mutate } = useSWR(
    id ? mediaPlayerLogURL : null,
    fetcher,
    { refreshInterval: 1000 }
  )
  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

export async function deleteMediaPlayerLog(id: number): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_ROOT}/mediaplayer/${id}/log`, {
    method: 'DELETE',
  })
  return handleResponse(res)
}

// Code Injection

export type CodeToInject = {
  code: string,
  mediaPlayerId: number,
  type: string,
}

export async function createCodeInjection(codeToInject: CodeToInject): Promise<MediaPlayerCodeInjection> {
  const { mediaPlayerId, code, type } = codeToInject
  const res = await fetch(`${API_ROOT}/mediaplayer/${mediaPlayerId}/inject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, type }),
  })
  const updated = await res.json()
  return updated
}

export type InjectManyRequest = {
  exhibitionId?: number;
  workId?: number;
  type: 'raw' | 'command';
  code: string;
}
export async function createManyCodeInjections(data: InjectManyRequest): Promise<MediaPlayerCodeInjection[]> {
  const res = await fetch(`${API_ROOT}/mediaplayer/inject-many`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  const injectionActions = await res.json()
  return injectionActions
}

export async function getBasicConfig(): Promise<Response> {
  const config = await fetch(`${API_ROOT}/mediaplayer/basicConfig`, {
    method: 'GET',
  })
  return config
}

export type CreateOrUpdateTimelineEventResponse = {
  data?: TimelineEvent
  error?: HttpError
}

export type DeleteTimelineEventResponse = {
  data?: { message: 'success' }
  error?: HttpError
}

export async function createMediaPlayerTimelineEvent(timelineEvent: Omit<TimelineEvent, 'id'>, mediaPlayerId: number): Promise<CreateOrUpdateTimelineEventResponse> {
  const res = await fetch(`${API_ROOT}/mediaplayer/${mediaPlayerId}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(timelineEvent),
  })
  return handleResponse(res)
}

export async function updateMediaPlayerTimelineEvent(timelineEvent: TimelineEvent, mediaPlayerId: number): Promise<CreateOrUpdateTimelineEventResponse> {
  const res = await fetch(`${API_ROOT}/mediaplayer/${mediaPlayerId}/events`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(timelineEvent),
  })
  return handleResponse(res)
}

export async function deleteMediaPlayerTimelineEvent(timelineEventId: number, mediaPlayerId: number): Promise<DeleteTimelineEventResponse> {
  const res = await fetch(`${API_ROOT}/mediaplayer/${mediaPlayerId}/events`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ timelineEventId }),
  })
  return handleResponse(res)
}

export function useMediaplayerTimelineEvents(mediaPlayerId: number): SwrData<TimelineEvent[]> {
  const mediaPlayerEventUrl = `${API_ROOT}/mediaplayer/${mediaPlayerId}/events`
  const { data, error, mutate } = useSWR(mediaPlayerEventUrl, fetcher)
  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

// Sync
type TimeDifference = {
  offset: number;
  resTime: number;
}

export async function requestTimeDifference(
  currentTimeCalculator: () => number
): Promise<TimeDifference> {
  // Accepts a monotonic clock fn as argument
  const res = await fetch(`${API_ROOT}/sync?reqSentAt=${currentTimeCalculator()}`)
  const resReceivedAt = currentTimeCalculator()
  const data = await res.json()
  const roundTripTime = resReceivedAt - data.reqSentAt
  const serverPerformance = data.resSentAt - data.reqReceivedAt
  const travelTime = roundTripTime - serverPerformance
  const oneWayLatency = travelTime / 2
  const serverTimeOffset = data.reqReceivedAt - data.reqSentAt - oneWayLatency
  return {
    offset: serverTimeOffset,
    resTime: roundTripTime,
  }
}

export const simulateTimestamp = async (id: string, password: string): Promise<{ now: number }> => {
  const now = Date.now()
  await fetch(`${API_ROOT}/work/${id}/timestamp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lastTimestamp: now, password }),
  })
  return { now }
}

export const resetTimestamp = async (id: number) => {
  await fetch(`${API_ROOT}/work/${id}/timestamp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lastTimestamp: 0 }),
  })
}

/*
export const updateTimestamp = async (id: string, password: string, timestamp: number) => {
  const res = await fetch(`${API_ROOT}/work/${id}/timestamp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lastTimestamp: timestamp, password }),
  })
  return { timestamp, res }
}
*/

export interface ConfigWithFences extends Config {
  geoFence?: GeoFence
  netmaskFence?: NetmaskFence
}

export function useConfig(): SwrData<ConfigWithFences> {
  // only authed users will be able to view inactive exhibitions and shows
  const url = `${API_ROOT}/config`
  const { data, error, mutate } = useSWR(url, fetcher)

  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

type UpdateConfigData = {
  error?: HttpError;
  data?: ConfigWithFences;
}

export async function updateConfig(newConfig: ConfigWithFences, oldConfig: ConfigWithFences): Promise<UpdateConfigData> {
  const res = await fetch(`${API_ROOT}/config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newConfig, oldConfig }),
  })
  return handleResponse(res)
}

export type FeatureFlags = {
  [feature in Feature]?: boolean
}
export type NewTenantRequestData = {
  serverSubdomain: string;
  organizationName: string;
  allowedEmails: { value: string }[];
  allowedEmailDomain?: string;
  externalAlias?: string;
  tier: Tier;
} & FeatureFlags

export type NewCommunityTenantRequestData = {
  serverSubdomain: string;
  organizationName: string;
  email: string;
  inviteCode?: string;
}

export async function createTenant(body: NewTenantRequestData): Promise<Response> {
  return fetch(`${API_ROOT}/tenant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export async function createCommunityTenant(body: NewCommunityTenantRequestData): Promise<Response> {
  return fetch(`${API_ROOT}/community`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export async function slackSuccessNotification(message: string): Promise<void> {
  try {
    const res = await fetch(process.env.SLACK_NOTIFICATION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: message }),
    })

    if (!res.ok) {
      logger.error({ statusMessage: res.statusText }, 'Failed to send slack notification')
    }

    logger.info({ statusMessage: res.statusText }, 'Slack notification sent')
  } catch (err) {
    logger.error({ err }, 'Error sending Slack notification')
  }
}

export async function updateTenant(tenantId, body: NewTenantRequestData): Promise<Response> {
  return fetch(`${API_ROOT}/tenant/${tenantId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export const tenantWithConfigAndAllowedEmails = Prisma.validator<Prisma.TenantArgs>()({
  include: { config: true, allowedEmails: true },
})

export type TenantWithConfigAndAllowedEmails = Prisma.TenantGetPayload<typeof tenantWithConfigAndAllowedEmails>

export function useTenants(): SwrData<TenantWithConfigAndAllowedEmails[]> {
  const url = `${API_ROOT}/tenant`
  const { data, error, mutate } = useSWR(url, fetcher)

  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

export function useTenant(id: number): SwrData<TenantWithConfigAndAllowedEmails> {
  const url = `${API_ROOT}/tenant/${id}`
  const { data, error, mutate } = useSWR(url, fetcher)

  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

export const useTenantFeatures = (): SwrData<Feature[]> => {
  const url = `${API_ROOT}/tenant/features`
  const { data, error, mutate } = useSWR(url, fetcher)

  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

export type InviteUserData = {
  allowedEmails: { email: string }[]
  allowedEmailDomain: string | null
}

export function useEmailAllowlist(): SwrData<InviteUserData> {
  const url = `${API_ROOT}/invite`
  const { data, error, mutate } = useSWR(url, fetcher)
  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

export type BackupDatabaseData = {
  config: Config,
  mediaPlayers: MediaPlayer[],
  works: (Work & { srts: SRT[] } & { tracks: Track[] })[],
  exhibitions: Exhibition[]
}

type LegacyBackupResponse = {
  error?: HttpError;
  data?: BackupDatabaseData;
}

export async function getLegacyBackup(): Promise<LegacyBackupResponse> {
  const res = await fetch(`${API_ROOT}/backup`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  return handleResponse(res)
}

type MigrateCaptionsResponse = {
  error?: HttpError;
  data?: WorkWithParsedSrts[];
}


export async function migrateCaptions(): Promise<MigrateCaptionsResponse> {
  const res = await fetch(`${API_ROOT}/restore/captions`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  return handleResponse(res)
}

type BackupDatabaseResponse = {
  error?: HttpError;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any
}

export async function restoreBackupDatabase(body: BackupDatabaseData): Promise<BackupDatabaseResponse> {
  const res = await fetch(`${API_ROOT}/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleResponse(res)
}

export type AudioUploadRequestArgs = {
  workingDirectory: string,
  cloudDirectory: string,
  file: string | Blob,
  filename: string,
  workId?: number
}

type UploadFileData = {
  error?: HttpError | Error;
  data?: AudioUploadResults;
}

export async function uploadAndProcessAudioFiles({
  workingDirectory,
  cloudDirectory,
  file,
  filename,
  workId,
}: AudioUploadRequestArgs): Promise<UploadFileData> {
  const body = new FormData()
  if (!file || !workingDirectory || !cloudDirectory) {
    return { error: new Error('Missing data needed for file upload and processing.') }
  }
  body.append('file', file, filename)
  body.append('workingDirectory', workingDirectory)
  body.append('cloudDirectory', cloudDirectory)
  if (workId) body.append('workId', String(workId))
  const res = await fetch(`${API_ROOT}/upload`, {
    method: 'POST',
    body,
  })
  return handleResponse(res)
}

export function useAvailableLanguages(): SwrData<LanguageOption[]> {
  const langUrl = `${API_ROOT}/translation/languages`
  const { data, error, mutate } = useSWR(langUrl, fetcher)
  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}

type GeoRestrictedResponse = {
  restricted: boolean
}

// Checks both netmask and geofence
// nb: geofence could also just be calculated on the FE
export function useRestrictionQuery(coords: Pick<GeolocationCoordinates, 'latitude' | 'longitude'>): SwrData<GeoRestrictedResponse> {
  const geoUrl = `${API_ROOT}/access-restriction${coords ? `?lat=${coords.latitude}&lon=${coords.longitude}` : ''}`
  const { data, error, mutate } = useSWR(geoUrl, fetcher)
  return {
    data,
    isLoading: !error && !data,
    isError: !!error,
    error,
    mutate,
  }
}
